// Street life: traffic lights, pedestrians, traffic cars and the police, plus the Good/Bad meter rules.
// Runs locally on every client for the cars that client owns (its player, and bots on the host); the
// people and traffic are cosmetic and local, so nothing here needs network sync.

import { Rng } from './rng';
import { Ground, Surface } from './ground';
import { Navigator } from './nav';
import { BotDriver } from './bot';
import { DEFAULT_TUNING, newCarState, stepCar, type CarState, type CarTuning } from './car';
import type { Match, PlayerState } from './match';
import { DEFAULT_FLAGS, type Flags } from './flags';

export interface Intersection { id: number; x: number; z: number; lights: { x: number; y: number; z: number; yaw: number }[]; }
export type LightPhase = 'ns' | 'ew';
export interface Pedestrian { id: number; x: number; y: number; z: number; yaw: number; speed: number; goalX: number; goalZ: number; state: 'walk' | 'fly' | 'gone'; vx: number; vy: number; vz: number; until: number; colour: number; }
export interface TrafficCar { id: number; car: CarState; driver: BotDriver; model: number; tuning: CarTuning; retargetAt: number; stopped: number; }
export interface PoliceCar { id: number; car: CarState; driver: BotDriver; target: string; contact: number; backoffUntil: number; tuning: CarTuning; }

export const KARMA = {
  stopAtRed: 5, deliver: 1, letCross: 2, runRed: -5, hitPed: -10, hitTraffic: -3,
  decayPer5s: 1, goodAt: 30, saintAt: 70, badAt: -30, menaceAt: -70,
  fine: 40, policeContactSeconds: 2, policeBackoff: 20, policeGiveUpSeconds: 10,
  lightPhase: 8, lightAmber: 2,
} as const;

export interface StreetEvent { t: 'karma'; pid: string; delta: number; reason: string; }
export type StreetEvents = (StreetEvent | { t: 'pedHit'; pid: string; ped: Pedestrian } | { t: 'fined'; pid: string; amount: number } | { t: 'police'; pid: string; on: boolean })[];

export class Streets {
  readonly intersections: Intersection[] = [];
  readonly peds: Pedestrian[] = [];
  readonly traffic: TrafficCar[] = [];
  readonly police: PoliceCar[] = [];
  /** feature flags (karma off = no meter changes, no police) */
  flags: Flags = DEFAULT_FLAGS;
  readonly events: StreetEvents = [];
  private rng: Rng;
  private nextPedId = 1; private nextCarId = 1;
  private pavementCells: number[] = [];
  private roadSpots: { x: number; z: number }[] = [];
  /** per player+intersection memory for the red light rules */
  private redMemory = new Map<string, { stoppedFor: number; wasRed: boolean; crossed: boolean; lastSeen: number }>();
  private karmaDecayAcc = 0;

  constructor(readonly ground: Ground, readonly nav: Navigator, readonly bounds: { x: [number, number]; z: [number, number] },
    lights: { p: [number, number, number]; yaw: number }[], seed: number, public pedCount = 70, public trafficCount = 12) {
    this.rng = new Rng(seed ^ 0x57ee7);
    // cluster lights into intersections
    for (const l of lights) {
      let found = this.intersections.find((i) => Math.hypot(i.x - l.p[0], i.z - l.p[2]) < 22);
      if (!found) { found = { id: this.intersections.length, x: l.p[0], z: l.p[2], lights: [] }; this.intersections.push(found); }
      found.lights.push({ x: l.p[0], y: l.p[1], z: l.p[2], yaw: l.yaw });
      const n = found.lights.length; found.x += (l.p[0] - found.x) / n; found.z += (l.p[2] - found.z) / n;
    }
    // snap intersection centres to the road
    for (const i of this.intersections) { const r = ground.nearestRoad(i.x, i.z, 20); if (r) { i.x = r.x; i.z = r.z; } }
    for (let r = 0; r < ground.rows; r++) for (let c = 0; c < ground.cols; c++) {
      const idx = r * ground.cols + c; const x = ground.origin[0] + (c + 0.5) * ground.cell, z = ground.origin[1] + (r + 0.5) * ground.cell;
      if (!this.inside(x, z)) continue;
      if (ground.types[idx] === Surface.pavement) this.pavementCells.push(idx);
      else if (ground.types[idx] === Surface.road && (r + c) % 7 === 0) this.roadSpots.push({ x, z });
    }
  }

  private inside(x: number, z: number): boolean { return x > this.bounds.x[0] + 5 && x < this.bounds.x[1] - 5 && z > this.bounds.z[0] + 5 && z < this.bounds.z[1] - 5; }
  private cellPos(idx: number): { x: number; z: number } { const g = this.ground; return { x: g.origin[0] + ((idx % g.cols) + 0.5) * g.cell, z: g.origin[1] + (Math.floor(idx / g.cols) + 0.5) * g.cell }; }

  // ---------------------------------------------------------------- traffic lights
  /** 'ns' means traffic moving along z has green. Cycle: ns green, amber, ew green, amber. */
  phaseAt(intersection: Intersection, time: number, saintFor?: { x: number; z: number; yaw: number } | null): { green: LightPhase; amber: boolean } {
    const cycle = (KARMA.lightPhase + KARMA.lightAmber) * 2;
    const t = ((time + intersection.id * 3.7) % cycle + cycle) % cycle;
    let green: LightPhase = t < KARMA.lightPhase + KARMA.lightAmber ? 'ns' : 'ew';
    const amber = (t % (KARMA.lightPhase + KARMA.lightAmber)) > KARMA.lightPhase;
    if (saintFor && Math.hypot(saintFor.x - intersection.x, saintFor.z - intersection.z) < 30) green = axisOf(saintFor.yaw);
    return { green, amber };
  }

  private redFor(car: CarState, inter: Intersection, time: number, saint: boolean): boolean {
    const ph = this.phaseAt(inter, time, saint ? car : null);
    return ph.green !== axisOf(car.yaw) || ph.amber;
  }

  // ---------------------------------------------------------------- spawning
  private spawnPed(near?: { x: number; z: number }): void {
    for (let i = 0; i < 20; i++) {
      const idx = this.pavementCells[this.rng.int(this.pavementCells.length)]; const p = this.cellPos(idx);
      if (near && Math.hypot(p.x - near.x, p.z - near.z) > 140) continue;
      if (near && Math.hypot(p.x - near.x, p.z - near.z) < 25) continue;
      const goal = this.pickPavementGoal(p.x, p.z);
      this.peds.push({ id: this.nextPedId++, x: p.x, y: this.ground.heightAt(p.x, p.z), z: p.z, yaw: 0, speed: 0.9 + this.rng.next() * 0.8, goalX: goal.x, goalZ: goal.z, state: 'walk', vx: 0, vy: 0, vz: 0, until: 0, colour: this.rng.int(8) });
      return;
    }
  }
  private pickPavementGoal(x: number, z: number): { x: number; z: number } {
    for (let i = 0; i < 12; i++) {
      const ang = this.rng.next() * Math.PI * 2, d = 10 + this.rng.next() * 30;
      const gx = x + Math.cos(ang) * d, gz = z + Math.sin(ang) * d;
      if (this.ground.surfaceAt(gx, gz) === Surface.pavement) return { x: gx, z: gz };
    }
    return { x, z };
  }
  private spawnTraffic(near?: { x: number; z: number }): void {
    for (let i = 0; i < 20; i++) {
      const s = this.roadSpots[this.rng.int(this.roadSpots.length)];
      if (near && (Math.hypot(s.x - near.x, s.z - near.z) > 220 || Math.hypot(s.x - near.x, s.z - near.z) < 40)) continue;
      const yaw = this.ground.roadHeading(s.x, s.z) + (this.rng.next() < 0.5 ? 0 : Math.PI);
      const car = newCarState(s.x, this.ground.heightAt(s.x, s.z), s.z, yaw);
      const driver = new BotDriver(this.nav, { skill: 0.45 + this.rng.next() * 0.3 });
      const goal = this.roadSpots[this.rng.int(this.roadSpots.length)]; driver.setGoal(goal.x, goal.z);
      this.traffic.push({ id: this.nextCarId++, car, driver, model: this.rng.int(15), tuning: { ...DEFAULT_TUNING, maxSpeed: 9 + this.rng.next() * 4, accel: 5 }, retargetAt: 0, stopped: 0 });
      return;
    }
  }

  // ---------------------------------------------------------------- step
  step(match: Match, owned: PlayerState[], dt: number, focus: { x: number; z: number }): void {
    const time = match.time;
    if (match.phase !== 'playing') return;
    // keep the population up near the focus (local player)
    while (this.peds.length < this.pedCount) this.spawnPed(focus);
    if (this.peds.length > this.pedCount) this.peds.splice(this.pedCount);
    while (this.traffic.length < this.trafficCount) this.spawnTraffic(focus);
    if (this.traffic.length > this.trafficCount) this.traffic.splice(this.trafficCount);
    this.stepPeds(match, owned, dt, focus);
    this.stepTraffic(match, owned, dt, focus, time);
    this.stepLights(match, owned, dt, time);
    this.stepPolice(match, owned, dt, time);
    // karma decay
    this.karmaDecayAcc += dt;
    if (this.karmaDecayAcc >= 5) { this.karmaDecayAcc -= 5; for (const p of owned) { if (p.karma > 0) p.karma = Math.max(0, p.karma - KARMA.decayPer5s); else if (p.karma < 0) p.karma = Math.min(0, p.karma + KARMA.decayPer5s); } }
  }

  addKarma(p: PlayerState, delta: number, reason: string): void {
    if (!this.flags.karma) return;
    const d = delta > 0 ? delta * p.perks.karmaGain : delta;
    p.karma = Math.max(-100, Math.min(100, p.karma + d));
    p.stats.karmaMin = Math.min(p.stats.karmaMin, p.karma); p.stats.karmaMax = Math.max(p.stats.karmaMax, p.karma);
    this.events.push({ t: 'karma', pid: p.id, delta: d, reason });
  }

  private stepPeds(match: Match, owned: PlayerState[], dt: number, focus: { x: number; z: number }): void {
    const g = this.ground;
    for (let i = this.peds.length - 1; i >= 0; i--) {
      const ped = this.peds[i];
      if (ped.state === 'gone' || Math.hypot(ped.x - focus.x, ped.z - focus.z) > 200) { this.peds.splice(i, 1); continue; }
      if (ped.state === 'fly') {
        ped.vy -= 20 * dt; ped.x += ped.vx * dt; ped.z += ped.vz * dt; ped.y += ped.vy * dt;
        const gy = g.heightAt(ped.x, ped.z); if (ped.y < gy) { ped.y = gy; ped.vx *= 0.3; ped.vz *= 0.3; ped.vy = 0; }
        if (match.time > ped.until) ped.state = 'gone';
        continue;
      }
      // dodge good drivers, otherwise walk to the goal
      let dodgeX = 0, dodgeZ = 0;
      for (const p of owned) {
        const dx = ped.x - p.car.x, dz = ped.z - p.car.z; const d = Math.hypot(dx, dz);
        if (d < 1.6 && Math.abs(p.car.speed) > 3 && p.respawnAt == null && p.ghostUntil <= match.time) {
          // bowled over
          ped.state = 'fly'; ped.vx = p.car.vx * 0.9 + dx / d * 4; ped.vz = p.car.vz * 0.9 + dz / d * 4; ped.vy = 7; ped.until = match.time + 2.2;
          p.stats.pedestrians++; this.addKarma(p, KARMA.hitPed, 'hit a pedestrian'); this.events.push({ t: 'pedHit', pid: p.id, ped });
          break;
        }
        const approaching = (p.car.vx * -dx + p.car.vz * -dz) > 0;
        if (d < 9 && approaching && (p.karma >= KARMA.goodAt || (p.perks.hornShove && p.hornUntil && p.hornUntil > match.time))) { dodgeX += dx / d; dodgeZ += dz / d; }
      }
      if (ped.state !== 'walk') continue;
      let mx: number, mz: number;
      if (dodgeX || dodgeZ) { const l = Math.hypot(dodgeX, dodgeZ); mx = dodgeX / l * 3; mz = dodgeZ / l * 3; }
      else {
        const dx = ped.goalX - ped.x, dz = ped.goalZ - ped.z; const d = Math.hypot(dx, dz);
        if (d < 1.5) { const gl = this.pickPavementGoal(ped.x, ped.z); ped.goalX = gl.x; ped.goalZ = gl.z; continue; }
        mx = dx / d * ped.speed; mz = dz / d * ped.speed;
      }
      const nx = ped.x + mx * dt, nz = ped.z + mz * dt;
      const s = g.surfaceAt(nx, nz);
      if (s === Surface.pavement || s === Surface.grass || (dodgeX || dodgeZ)) { ped.x = nx; ped.z = nz; ped.yaw = Math.atan2(mx, mz); }
      else { const gl = this.pickPavementGoal(ped.x, ped.z); ped.goalX = gl.x; ped.goalZ = gl.z; }
      ped.y = g.heightAt(ped.x, ped.z);
    }
  }

  private stepTraffic(match: Match, owned: PlayerState[], dt: number, focus: { x: number; z: number }, time: number): void {
    const others = [...owned.map((p) => ({ x: p.car.x, z: p.car.z, r: p.tuning.radius + p.tuning.halfLength * 0.5, vx: p.car.vx, vz: p.car.vz })), ...this.traffic.map((t) => ({ x: t.car.x, z: t.car.z, r: 1.7, vx: t.car.vx, vz: t.car.vz }))];
    for (let i = this.traffic.length - 1; i >= 0; i--) {
      const t = this.traffic[i];
      if (Math.hypot(t.car.x - focus.x, t.car.z - focus.z) > 260) { this.traffic.splice(i, 1); continue; }
      if (time >= t.retargetAt || t.driver.distanceToGoal(t.car) < 8) { const goal = this.roadSpots[this.rng.int(this.roadSpots.length)]; t.driver.setGoal(goal.x, goal.z); t.retargetAt = time + 40; }
      const inp = t.driver.step(t.car, dt);
      // stop at red lights
      for (const inter of this.intersections) {
        const dx = inter.x - t.car.x, dz = inter.z - t.car.z; const d = Math.hypot(dx, dz);
        if (d < 16 && d > 5 && (Math.sin(t.car.yaw) * dx + Math.cos(t.car.yaw) * dz) > 0 && this.redFor(t.car, inter, time, false)) { inp.brake = 1; break; }
      }
      // pull over for good drivers coming up behind, shoved by a Horn of Doom
      for (const p of owned) {
        const dx = t.car.x - p.car.x, dz = t.car.z - p.car.z; const d = Math.hypot(dx, dz);
        const behind = (Math.sin(p.car.yaw) * dx + Math.cos(p.car.yaw) * dz) > 0;
        if (d < 14 && behind && p.karma >= KARMA.goodAt) { inp.steer = Math.max(inp.steer, 0.5); inp.brake = Math.max(inp.brake, 0.6); }
        if (d < 10 && p.perks.hornShove && p.hornUntil && p.hornUntil > time) { t.car.vx += dx / d * 6 * dt * 10; t.car.vz += dz / d * 6 * dt * 10; }
      }
      const mine = others.filter((o) => o.x !== t.car.x || o.z !== t.car.z);
      stepCar(t.car, inp, t.tuning, this.ground, this.bounds, dt, mine);
      // karma for hitting traffic (the player's own impact marks it)
      for (const p of owned) {
        if (p.car.impactKind !== 'car' || p.car.impact < 4) continue;
        if (Math.hypot(p.car.x - t.car.x, p.car.z - t.car.z) < p.tuning.radius + 1.3 + 0.6 && time - (p.lastTrafficHit ?? -9) > 1.5) { p.lastTrafficHit = time; this.addKarma(p, KARMA.hitTraffic, 'hit a car'); }
      }
      others[owned.length + i] = { x: t.car.x, z: t.car.z, r: 1.7, vx: t.car.vx, vz: t.car.vz };
    }
  }

  /** Traffic and police cars as collision circles for the owned players' physics. */
  collisionCircles(): { x: number; z: number; r: number; vx: number; vz: number }[] {
    return [...this.traffic.map((t) => ({ x: t.car.x, z: t.car.z, r: 1.7, vx: t.car.vx, vz: t.car.vz })), ...this.police.map((t) => ({ x: t.car.x, z: t.car.z, r: 1.7, vx: t.car.vx, vz: t.car.vz }))];
  }

  private stepLights(_match: Match, owned: PlayerState[], dt: number, time: number): void {
    for (const p of owned) {
      if (p.respawnAt != null) continue;
      const saint = p.karma >= KARMA.saintAt;
      for (const inter of this.intersections) {
        const dx = inter.x - p.car.x, dz = inter.z - p.car.z; const d = Math.hypot(dx, dz);
        const key = `${p.id}:${inter.id}`;
        let mem = this.redMemory.get(key);
        if (d > 30) { if (mem) { this.redMemory.delete(key); } continue; }
        if (!mem) { mem = { stoppedFor: 0, wasRed: false, crossed: false, lastSeen: time }; this.redMemory.set(key, mem); }
        mem.lastSeen = time;
        const ahead = (Math.sin(p.car.yaw) * dx + Math.cos(p.car.yaw) * dz) > 0;
        const red = this.redFor(p.car, inter, time, saint);
        if (d > 6 && d < 22 && ahead) {
          if (red) { mem.wasRed = true; if (Math.abs(p.car.speed) < 1) mem.stoppedFor += dt; }
          else if (mem.wasRed && mem.stoppedFor >= 1 && !mem.crossed) { mem.crossed = true; this.addKarma(p, KARMA.stopAtRed, 'waited at a red light'); p.stats.redLightsObeyed++; }
        }
        if (d <= 6 && !mem.crossed) {
          mem.crossed = true;
          if (red && Math.abs(p.car.speed) > 5 && mem.stoppedFor < 1) { this.addKarma(p, KARMA.runRed, 'ran a red light'); p.stats.redLightsRun++; }
        }
      }
    }
  }

  private stepPolice(match: Match, owned: PlayerState[], dt: number, time: number): void {
    // spawn / despawn per owned player based on karma
    for (const p of owned) {
      const want = !this.flags.karma ? 0 : p.karma <= KARMA.menaceAt ? 2 : p.karma <= KARMA.badAt ? 1 : 0;
      const mine = this.police.filter((c) => c.target === p.id);
      if (mine.length < want && p.respawnAt == null) {
        const spot = this.spawnSpotAway(p.car.x, p.car.z, 90);
        if (spot) {
          const car = newCarState(spot.x, this.ground.heightAt(spot.x, spot.z), spot.z, this.ground.roadHeading(spot.x, spot.z));
          this.police.push({ id: this.nextCarId++, car, driver: new BotDriver(this.nav, { skill: 0.9, topSpeed: 0.85 }), target: p.id, contact: 0, backoffUntil: 0, tuning: { ...DEFAULT_TUNING, maxSpeed: 38, accel: 18 } });
          this.events.push({ t: 'police', pid: p.id, on: true });
        }
      }
      if (mine.length > want) {
        // linger a while after karma recovers
        const c = mine[mine.length - 1]; c.giveUpAt ??= time + KARMA.policeGiveUpSeconds * p.perks.policePatience;
        if (time >= c.giveUpAt) { this.police.splice(this.police.indexOf(c), 1); if (!this.police.some((x) => x.target === p.id)) this.events.push({ t: 'police', pid: p.id, on: false }); }
      } else for (const c of mine) c.giveUpAt = undefined;
    }
    const circles = [...owned.map((p) => ({ x: p.car.x, z: p.car.z, r: p.tuning.radius + p.tuning.halfLength * 0.5, vx: p.car.vx, vz: p.car.vz })), ...this.traffic.map((t) => ({ x: t.car.x, z: t.car.z, r: 1.7, vx: t.car.vx, vz: t.car.vz }))];
    for (const c of this.police) {
      const target = match.players.get(c.target); if (!target) continue;
      const d = Math.hypot(target.car.x - c.car.x, target.car.z - c.car.z);
      if (time < c.backoffUntil) { if (!c.driver.hasGoal() || c.driver.distanceToGoal(c.car) < 10) { const s = this.spawnSpotAway(target.car.x, target.car.z, 120); if (s) c.driver.setGoal(s.x, s.z); } }
      else c.driver.setGoal(target.car.x + target.car.vx * 0.8, target.car.z + target.car.vz * 0.8);
      const inp = c.driver.step(c.car, dt);
      stepCar(c.car, inp, c.tuning, this.ground, this.bounds, dt, circles);
      if (time >= c.backoffUntil && d < 4.2 && target.respawnAt == null) {
        c.contact += dt;
        if (c.contact >= KARMA.policeContactSeconds) {
          c.contact = 0; c.backoffUntil = time + KARMA.policeBackoff;
          const fine = Math.min(target.money, KARMA.fine); target.money -= fine;
          this.events.push({ t: 'fined', pid: target.id, amount: fine });
        }
      } else c.contact = Math.max(0, c.contact - dt * 0.5);
    }
  }

  private spawnSpotAway(x: number, z: number, dist: number): { x: number; z: number } | null {
    for (let i = 0; i < 30; i++) {
      const s = this.roadSpots[this.rng.int(this.roadSpots.length)]; const d = Math.hypot(s.x - x, s.z - z);
      if (d > dist * 0.7 && d < dist * 1.6) return s;
    }
    return null;
  }

  drainEvents(): StreetEvents { return this.events.splice(0); }
}

/** Which light axis a heading uses: 'ns' when moving mostly along z. */
export function axisOf(yaw: number): LightPhase { return Math.abs(Math.cos(yaw)) >= Math.abs(Math.sin(yaw)) ? 'ns' : 'ew'; }

declare module './match' {
  interface PlayerState { fireTick?: number; hornUntil?: number; lastTrafficHit?: number; }
}
declare module './streets' {
  interface PoliceCar { giveUpAt?: number; }
}
